import React from 'react'
import { graphql, MutationFunc } from 'react-apollo'
import { Experiment } from '@deal/experiment-js'
import { FancyModal, Avatar, FancyContent, FancyShowcase } from '@deal/components'
import Path from '#src/components/Path'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import {
  UserExpertReviewsComponent,
  StartExpertRatingPathMutation,
  StartExpertRatingPathMutationVariables,
  BusinessUserToRateFragment
} from '#src/generated/graphql'
import ShareFooter from '#src/components/Share/Footer'
import StartExpertRatingDocument from './StartExpertRating.gql'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import Heart from './heart.svg'
import styles from './styles.css'
import { toast } from 'react-toastify'
import { PathLayouts } from '#src/typings'

interface ExpertRatingProps {
  isOpen: boolean
  onCloseModal: () => void
  expertProfileId: string
  expertStarRating?: number
  allowMultipleReviews?: boolean
  orderId?: string
}

interface ExpertRatingState {
  businessUser?: BusinessUserToRateFragment
  flowId?: string
  pageSlug?: string
  pathSlug?: string
  pathCompleted: boolean
}

interface ExpertRatingCompleteProps {
  businessUser: BusinessUserToRateFragment
}

const ExpertRatingComplete: React.SFC<ExpertRatingCompleteProps> = ({ businessUser }) => (
  <Experiment id="expert-rating-complete" default="v1">
    {({ result }) => {
      let header: string
      let subheader: string
      switch (result) {
        case 'v2':
          header = `Love chatting with ${businessUser.firstName}? Share the love with $25 off!`
          subheader = `Share ${
            businessUser.firstName
          }'s profile with your friends and family. As a thank you, they’ll each get $25 off when they shop with ${
            businessUser.firstName
          }!`
          break
        case 'v3':
          header = `How great is ${businessUser.firstName}? Spread the word!`
          subheader = `Share ${
            businessUser.firstName
          }'s profile with your friends and family. As a thank you, they’ll each get $25 off when they shop with ${
            businessUser.firstName
          }!`
          break
        case 'v4':
          header = `Support ${businessUser.firstName}`
          subheader = `Share ${
            businessUser.firstName
          }'s profile with your friends. They’ll each get $25 when they shop with ${
            businessUser.firstName
          }!`
          break
        case 'v5':
          header = `Share ${businessUser.firstName}'s profile + $25 with your friends!`
          subheader = `Support ${
            businessUser.firstName
          } by spreading the word with your friends and family. They’ll each get $25 off when they shop with ${
            businessUser.firstName
          }.`
          break
        case 'v1':
        default:
          header = 'One more way to help your expert out'
          subheader = `Help support ${
            businessUser.firstName
          } by spreading the word with your friends and
            family. Share ${businessUser.firstName}'s profile and get them hooked up!`
          break
      }
      return (
        <div className={styles.feedback}>
          <div className={styles.feedbackHeader}>
            <div className={styles.heart}>
              <Heart />
            </div>
            {header}
          </div>
          <div className={styles.feedbackSep} />
          <div className={styles.feedbackBody}>{subheader}</div>
          <ShareFooter
            className={styles.share}
            type="expert"
            sharePath={`/experts/${
              businessUser.vanityId ? businessUser.vanityId : businessUser.id
            }`}
            expertId={businessUser.id}
            utmContent={businessUser.vanityId ? businessUser.vanityId : businessUser.id}
            variant="large"
          />
        </div>
      )
    }}
  </Experiment>
)

class ExpertRating extends React.Component<
  ExpertRatingProps & WithIdentityProps & Mutations & WithAnalyticsProps,
  ExpertRatingState
> {
  constructor(props: ExpertRatingProps & Mutations & WithAnalyticsProps & WithIdentityProps) {
    super(props)

    this.state = {
      pathCompleted: false
    }

    this.handlePathCompleted = this.handlePathCompleted.bind(this)
  }

  public componentDidMount() {
    const { myself } = this.props

    // Can't rate someone if you're not logged in
    if (!myself) {
      return
    }

    this.props.startExpertRating!({
      variables: {
        input: {
          expertId: this.props.expertProfileId,
          expertStartRating: this.props.expertStarRating
        }
      }
    })
      .then(response => {
        if (
          response &&
          response.data &&
          response.data.startExpertRatingPath &&
          response.data.startExpertRatingPath.flow
        ) {
          const flow = response.data.startExpertRatingPath!.flow!
          const firstPage = flow.pagesConnection.edges[0].node

          this.setState({
            businessUser: response.data.startExpertRatingPath.expert,
            flowId: flow.id,
            pageSlug: firstPage.slug,
            pathSlug: flow.slug
          })

          if (this.props.analytics) {
            this.props.analytics.expertRatingFlowStarted({
              expert_id: this.props.expertProfileId
            })
          }
        } else {
          throw new Error(
            response && response.errors
              ? response.errors.join(', ').toString()
              : 'Error starting expert profile rating flow'
          )
        }
      })
      .catch(error => {
        throw new Error(error.toString())
      })
  }

  public handlePathCompleted() {
    if (this.props.analytics) {
      this.props.analytics.expertRatingFlowCompleted({
        expert_id: this.props.expertProfileId
      })
    }

    this.setState({
      pathCompleted: true
    })
  }

  public render() {
    const { onCloseModal, isOpen, myself, expertStarRating, allowMultipleReviews } = this.props

    // Can't rate someone if you're not logged in
    if (!myself) {
      return null
    }

    if (!isOpen) {
      return null
    }

    if (this.state.businessUser) {
      const businessUser = this.state.businessUser
      const expertPhotoUrl =
        businessUser.expertProfile && businessUser.expertProfile.profilePhoto
          ? businessUser.expertProfile.profilePhoto.url
          : undefined

      return (
        <UserExpertReviewsComponent
          variables={{
            expertId: businessUser.id
          }}
        >
          {({ data }) => {
            if (data && data.me) {
              /**
               * TODO: tell user they have already reviewed this expert instead of just not showing modal
               */
              const hasUserReviewedExpert = data.me.user.expertReviews.find(
                expertReview => expertReview.expert.id === businessUser.id
              )
              const allowUserReview = allowMultipleReviews || !hasUserReviewedExpert

              if (allowUserReview) {
                return (
                  <FancyModal
                    isOpen={isOpen}
                    onRequestClose={onCloseModal}
                    modalContentClassName={styles.modalContent}
                  >
                    <FancyShowcase className={styles.expertShowcase}>
                      <Avatar
                        name={`${businessUser.firstName} ${businessUser.lastName}`}
                        size={160}
                        src={expertPhotoUrl}
                        className={styles.expertAvatar}
                      />

                      <div className={styles.expertDetails}>
                        <div className={styles.expertName}>{businessUser.firstName}</div>
                        {businessUser.expertProfile && (
                          <div className={styles.expertHeadline}>
                            {businessUser.expertProfile.headline}
                          </div>
                        )}
                      </div>
                    </FancyShowcase>
                    <FancyContent className={styles.details}>
                      {!this.state.pathCompleted && (
                        <Path
                          pathLayout={PathLayouts.MODAL}
                          pathSlug={this.state.pathSlug}
                          pageSlug={this.state.pageSlug}
                          flowId={this.state.flowId}
                          className={styles.pathContainer}
                          onPathComplete={this.handlePathCompleted}
                          prefillData={expertStarRating ? { expertStarRating } : {}}
                        />
                      )}
                      {this.state.pathCompleted && (
                        <ExpertRatingComplete businessUser={businessUser} />
                      )}
                    </FancyContent>
                  </FancyModal>
                )
              } else {
                toast.success('You have already provided a review.  Thank you!')
                return null
              }
            } else {
              return null
            }
          }}
        </UserExpertReviewsComponent>
      )
    }

    return null
  }
}

interface Mutations {
  startExpertRating?: MutationFunc<
    StartExpertRatingPathMutation,
    StartExpertRatingPathMutationVariables
  >
}

export default graphql<ExpertRatingProps & Mutations, {}>(StartExpertRatingDocument, {
  name: 'startExpertRating'
})(withIdentity(withAnalytics(ExpertRating)))
